import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChatKeys } from "web_myplann/Api/Chat/ChatKeys";
import {
	markChatMessageFailed,
	seedChatMessagesCache,
} from "web_myplann/Chat/Helpers/Messages/chatMessagesCache";
import { confirmOptimisticChatMessage } from "web_myplann/Chat/Helpers/Messages/confirmOptimisticChatMessage";
import { createOptimisticChatMessage } from "web_myplann/Chat/Helpers/Messages/createOptimisticChatMessage";
import { mapChatMessage } from "web_myplann/Chat/Helpers/Messages/mapChatMessage";
import { postChatsConversationsV2 } from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";

type CreateChatConversationVariables = {
	advisorId?: string;
	content: string;
	clientId: string;
};

export const useCreateChatConversationMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: ChatKeys.send(null),
		scope: { id: ChatKeys.sendScope(null) },
		mutationFn: async ({
			advisorId,
			content,
		}: CreateChatConversationVariables) => {
			const created = await processResponse(postChatsConversationsV2, {
				skipToastFor: true,
			})({
				agentIds: advisorId ? [advisorId] : [],
				content,
			});

			if (!created) {
				throw new Error("Chat conversation could not be created");
			}

			return created;
		},
		onMutate: ({ content, clientId }) => {
			const optimistic = createOptimisticChatMessage({ clientId, content });
			seedChatMessagesCache(queryClient, null, optimistic);

			return { optimistic };
		},
		onError: (_error, { clientId }) => {
			markChatMessageFailed(queryClient, null, clientId);
		},
		onSuccess: ({ conversationId, message }, { clientId }, { optimistic }) => {
			if (!queryClient.getQueryData(ChatKeys.messages(conversationId))) {
				seedChatMessagesCache(queryClient, conversationId, optimistic);
			}

			confirmOptimisticChatMessage(
				queryClient,
				conversationId,
				clientId,
				mapChatMessage(message),
			);

			queryClient.removeQueries({ queryKey: ChatKeys.messages(null) });
			queryClient.invalidateQueries({ queryKey: ChatKeys.list() });
		},
	});
};
